import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, X, RefreshCw, FileSpreadsheet, Layers } from 'lucide-react';
import { vouchersApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import { useAuth } from '../context/AuthContext';

type Row = {
  party_id: number; party_name: string; ledger_group_id: number | null; ledger_group_name: string | null;
  vch_id: number; vch_no: string | null; vch_date: string; bill_name: string | null;
  item_id: number | null; item_name: string; qty: number; rate: number; amount: number; gst_amount: number;
  credit_note: number | null; received: number | null;   // bill-level, on the first line of each bill
  comm_type: 'qty' | 'value' | 'cloud' | null; comm_rate: number | null; comm_amount: number | null;
  net_payable: number | null;   // On Value → gross − comm; On Qty → qty × comm
  commission_vch_no: string | null;   // commission voucher(s) posted for this bill
  commission_amount: number | null;   // commission amount posted against this bill
};
type Totals = { qty: number; amount: number; gst: number; commission: number; credit_note: number; received: number; net_payable: number; commission_posted: number; bills: number; parties: number };

const fmt = (n: any) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n: any) => Number(n || 0).toLocaleString('en-IN');
const fmtD = (s: any) => { const d = new Date(String(s)); return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }); };
const toInputDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const commTypeLabel = (t: Row['comm_type']) => (t === 'qty' ? 'On Qty' : t === 'value' ? 'On Value' : t === 'cloud' ? 'Cloud' : '—');

export default function PartyBillItemReport() {
  const { showError, showSuccess } = useToast();
  const { isAdmin } = useAuth();
  const admin = isAdmin();   // commission generation is admin-only
  // Default period: current month
  const now = new Date();
  const [dateFrom, setDateFrom] = useState<string>(toInputDate(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [dateTo, setDateTo] = useState<string>(toInputDate(now));
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [groupId, setGroupId] = useState<number | ''>('');
  const [groups, setGroups] = useState<Array<{ id: number; name: string; parent_id: number | null }>>([]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [totals, setTotals] = useState<Totals>({ qty: 0, amount: 0, gst: 0, commission: 0, credit_note: 0, received: 0, net_payable: 0, commission_posted: 0, bills: 0, parties: 0 });

  useEffect(() => {
    vouchersApi.getLedgerGroupTree().then(r => { if ((r as any)?.success) setGroups((r as any).data || []); }).catch(() => {});
  }, []);
  // Indented, ordered group list for the filter dropdown
  const flatGroups = useMemo(() => {
    const ids = new Set(groups.map(g => g.id));
    const byParent = new Map<number | null, Array<{ id: number; name: string; parent_id: number | null }>>();
    for (const g of groups) { const k = g.parent_id; if (!byParent.has(k)) byParent.set(k, []); byParent.get(k)!.push(g); }
    Array.from(byParent.values()).forEach(arr => arr.sort((a, b) => a.name.localeCompare(b.name)));
    const out: Array<{ id: number; label: string }> = [];
    const walk = (nodes: any[], depth: number, seen: Set<number>) => {
      for (const n of nodes) {
        if (seen.has(n.id)) continue; seen.add(n.id);
        out.push({ id: n.id, label: `${'  '.repeat(depth)}${depth ? '└ ' : ''}${n.name}` });
        walk(byParent.get(n.id) || [], depth + 1, seen);
      }
    };
    // Roots = groups with no parent OR whose parent is outside the visible set.
    // For a group-scoped user (e.g. "Rohit Sharma"), the parent ("Sundry
    // Debtors") isn't in scope, so treat that group as a root — otherwise its
    // sub-groups never render and the user can't filter them.
    const roots = groups.filter(g => g.parent_id == null || !ids.has(g.parent_id)).sort((a, b) => a.name.localeCompare(b.name));
    walk(roots, 0, new Set());
    return out;
  }, [groups]);

  useEffect(() => { const t = setTimeout(() => setDebounced(search), 300); return () => clearTimeout(t); }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vouchersApi.getPartyBillItemReport({
        date_from: dateFrom || undefined, date_to: dateTo || undefined,
        search: debounced || undefined, group_id: groupId ? Number(groupId) : undefined,
      });
      setRows((res.data?.rows || []) as Row[]);
      setTotals((res.data?.totals || { qty: 0, amount: 0, gst: 0, commission: 0, credit_note: 0, received: 0, net_payable: 0, commission_posted: 0, bills: 0, parties: 0 }) as Totals);
    } catch (e: any) {
      showError('Error', e?.message || 'Failed to load report');
    } finally { setLoading(false); }
  }, [dateFrom, dateTo, debounced, groupId, showError]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Bill-level selection → commission voucher generation ──
  // Rows are item-wise; aggregate net payable per bill (vch_id) and remember
  // the first row of each bill (where the checkbox is rendered).
  const billAgg = useMemo(() => {
    const map = new Map<number, { vch_id: number; vch_no: string | null; group_id: number | null; group_name: string | null; net_payable: number; commissioned: boolean }>();
    const firstIdx = new Map<number, number>();
    rows.forEach((r, i) => {
      if (!map.has(r.vch_id)) { map.set(r.vch_id, { vch_id: r.vch_id, vch_no: r.vch_no, group_id: r.ledger_group_id, group_name: r.ledger_group_name, net_payable: 0, commissioned: !!r.commission_vch_no }); firstIdx.set(r.vch_id, i); }
      map.get(r.vch_id)!.net_payable += Number(r.net_payable) || 0;
    });
    return { map, firstIdx };
  }, [rows]);
  // Selectable = positive net payable AND not already commissioned (no re-generate).
  const selectableBillIds = useMemo(() => Array.from(billAgg.map.values()).filter(b => Math.round(b.net_payable) > 0 && !b.commissioned).map(b => b.vch_id), [billAgg]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [posting, setPosting] = useState(false);
  useEffect(() => { setSelected(new Set()); }, [rows]);   // reset on new data

  const toggleBill = (id: number) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(prev => prev.size === selectableBillIds.length ? new Set() : new Set(selectableBillIds));

  const selSummary = useMemo(() => {
    let amount = 0; const groups = new Set<string>();
    selected.forEach(id => { const b = billAgg.map.get(id); if (b) { amount += Math.round(b.net_payable); groups.add((b.group_name || '').trim() || `#${b.group_id}`); } });
    return { count: selected.size, amount, groups: groups.size };
  }, [selected, billAgg]);

  const handleGenerateCommission = async () => {
    if (selected.size === 0) return;
    const bills = Array.from(selected).map(id => {
      const b = billAgg.map.get(id)!;
      return { vch_id: b.vch_id, vch_no: b.vch_no, group_id: b.group_id, group_name: b.group_name, net_payable: Math.round(b.net_payable) };
    });
    const ok = window.confirm(
      `Generate commission ${selSummary.groups > 1 ? `— ${selSummary.groups} vouchers (one per group)` : 'voucher'} for ${selSummary.count} bill(s), total ₹${selSummary.amount.toLocaleString('en-IN')}?\n\nDr  Reseller Commission\nCr  Group ledger (bill-wise)`
    );
    if (!ok) return;
    setPosting(true);
    try {
      const res = await vouchersApi.createCommissionEntry({ bills });
      const created = res.data?.created || [], skipped = res.data?.skipped || [];
      if (created.length) showSuccess(`${created.length} commission voucher(s) created`, created.map(v => `${v.group}: ${v.vch_no} — ₹${v.amount.toLocaleString('en-IN')} (${v.bills} bills)`).join('   ·   '));
      if (skipped.length) showError('Some groups skipped', skipped.map(v => `${v.group}: ${v.reason}`).join('; '));
      if (!created.length && !skipped.length) showError('Nothing created', 'No valid bills to commission');
      setSelected(new Set());
      fetchData();
    } catch (e: any) {
      showError('Error', e?.message || 'Failed to create commission voucher');
    } finally { setPosting(false); }
  };

  const exportExcel = async () => {
    if (rows.length === 0) { showError('Export', 'No data to export'); return; }
    const { utils, writeFile } = await import('xlsx');
    const data = rows.map((r, i) => ({
      'Sr': i + 1,
      'Bill No': r.vch_no || '—',
      'Date': fmtD(r.vch_date),
      'Party Name': r.party_name,
      'Group Name': r.ledger_group_name || '',
      'Item Name': r.item_name,
      'Qty': Number(r.qty) || 0,
      'Rate': Number(r.rate) || 0,
      'Amount': Number(r.amount) || 0,
      'GST Amount': Math.round((Number(r.amount) || 0) + (Number(r.gst_amount) || 0)),
      'Credit Note': r.credit_note ?? '',
      'Received': r.received ?? '',
      'Comm Type': commTypeLabel(r.comm_type),
      'Comm Val': r.comm_amount ?? '',
      'Net Payable': r.net_payable ?? '',
      'Comm Vch No': r.commission_vch_no ?? '',
      'Comm Amount': r.commission_amount ?? '',
    }));
    data.push({ 'Sr': '' as any, 'Bill No': 'TOTAL' as any, 'Date': '', 'Party Name': `${totals.parties} parties` as any,
      'Group Name': `${totals.bills} bills` as any, 'Item Name': '', 'Qty': totals.qty, 'Rate': '' as any,
      'Amount': totals.amount, 'GST Amount': Math.round(totals.amount + totals.gst), 'Credit Note': totals.credit_note, 'Received': totals.received, 'Comm Type': '' as any, 'Comm Val': totals.commission, 'Net Payable': totals.net_payable, 'Comm Vch No': '' as any, 'Comm Amount': totals.commission_posted });
    const ws = utils.json_to_sheet(data);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Party Bill Item');
    writeFile(wb, `Party_Bill_Item_${dateFrom}_${dateTo}.xlsx`);
    showSuccess('Exported', 'Excel downloaded');
  };

  const th = 'px-2.5 py-2 text-[11px] font-bold text-gray-700 border border-gray-300 bg-slate-100 whitespace-nowrap';
  const td = 'px-2.5 py-1.5 text-[12.5px] border border-gray-200 whitespace-nowrap';

  return (
    <div className="p-3 sm:p-4">
      {/* Header + filters */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-blue-600" />
          <h1 className="text-lg font-bold text-gray-800">Party / Bill / Item Report</h1>
          <span className="text-[12px] text-gray-400 italic hidden sm:inline">Sales &amp; Credit Notes, item-wise</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={groupId} onChange={e => setGroupId(e.target.value ? Number(e.target.value) : '')}
            title="Filter by ledger group"
            className="border border-slate-300 rounded px-2 py-1.5 text-[13px] bg-white outline-none focus:ring-1 focus:ring-blue-300 max-w-[200px]">
            <option value="">All Ledger Groups</option>
            {flatGroups.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="border border-slate-300 rounded px-2 py-1.5 text-[13px] outline-none focus:ring-1 focus:ring-blue-300 bg-white" />
          <span className="text-[12px] text-gray-400">to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="border border-slate-300 rounded px-2 py-1.5 text-[13px] outline-none focus:ring-1 focus:ring-blue-300 bg-white" />
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search party…"
              className="pl-8 pr-8 py-1.5 border border-slate-300 rounded text-[13px] w-48 outline-none focus:ring-1 focus:ring-blue-300 bg-white" />
            {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={13} /></button>}
          </div>
          <button onClick={fetchData} className="p-2 rounded text-slate-500 hover:bg-slate-100" title="Refresh"><RefreshCw size={15} className={loading ? 'animate-spin' : ''} /></button>
          <button onClick={exportExcel} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-green-300 rounded text-[13px] text-green-700 bg-green-50 hover:bg-green-100"><FileSpreadsheet size={14} /> Excel</button>
          {admin && selected.size > 0 && (
            <button onClick={handleGenerateCommission} disabled={posting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[13px] font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
              title="Post Dr Reseller Commission / Cr Group (bill-wise) for the selected bills">
              {posting ? 'Posting…' : `Generate Commission · ${selSummary.count} bill${selSummary.count > 1 ? 's' : ''} · ₹${selSummary.amount.toLocaleString('en-IN')}${selSummary.groups > 1 ? ` · ${selSummary.groups} grp` : ''}`}
            </button>
          )}
        </div>
      </div>

      {/* Flat table */}
      <div className="overflow-auto border border-gray-300 rounded bg-white" style={{ maxHeight: 'calc(100vh - 180px)' }}>
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className={`${th} w-[34px] text-center`} title={admin ? 'Select bills for commission' : ''}>
                {admin && (
                  <input type="checkbox" className="accent-violet-600 cursor-pointer"
                    checked={selectableBillIds.length > 0 && selected.size === selectableBillIds.length}
                    ref={el => { if (el) el.indeterminate = selected.size > 0 && selected.size < selectableBillIds.length; }}
                    onChange={toggleAll} disabled={selectableBillIds.length === 0} />
                )}
              </th>
              <th className={`${th} w-[46px] text-center`}>Sr</th>
              <th className={`${th} text-left`}>Bill No</th>
              <th className={`${th} text-left`}>Date</th>
              <th className={`${th} text-left`}>Party Name</th>
              <th className={`${th} text-left`}>Group Name</th>
              <th className={`${th} text-left`}>Item Name</th>
              <th className={`${th} text-right`}>Qty</th>
              <th className={`${th} text-right`}>Rate</th>
              <th className={`${th} text-right`}>Amount</th>
              <th className={`${th} text-right`}>GST Amount</th>
              <th className={`${th} text-right`}>Credit Note</th>
              <th className={`${th} text-right`}>Received</th>
              <th className={`${th} text-center`}>Comm Type</th>
              <th className={`${th} text-right`}>Comm Val</th>
              <th className={`${th} text-right`}>Net Payable</th>
              <th className={`${th} text-left`}>Comm. Vch No</th>
              <th className={`${th} text-right`}>Comm. Amount</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={18} className="px-4 py-10 text-center text-slate-400 text-sm">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={18} className="px-4 py-10 text-center text-slate-400 text-sm">No sales found for this period</td></tr>
            ) : rows.map((r, i) => {
              const bill = billAgg.map.get(r.vch_id);
              const isBillFirstRow = billAgg.firstIdx.get(r.vch_id) === i;
              const billCommissioned = !!bill?.commissioned;
              const billSelectable = Math.round(Number(bill?.net_payable) || 0) > 0 && !billCommissioned;
              return (
                <tr key={`${r.vch_id}-${i}`} className={`${selected.has(r.vch_id) ? 'bg-violet-50' : i % 2 ? 'bg-slate-50/60' : 'bg-white'}`}>
                  <td className={`${td} text-center`}>
                    {admin && isBillFirstRow && (billSelectable ? (
                      <input type="checkbox" className="accent-violet-600 cursor-pointer"
                        checked={selected.has(r.vch_id)} onChange={() => toggleBill(r.vch_id)} />
                    ) : billCommissioned ? (
                      <span title="Commission already generated" className="text-violet-400">✓</span>
                    ) : null)}
                  </td>
                  <td className={`${td} text-center text-slate-400 tabular-nums`}>{i + 1}</td>
                  <td className={`${td} font-medium text-blue-700`} title={r.bill_name || undefined}>{r.vch_no || r.bill_name || '—'}</td>
                  <td className={`${td} text-gray-600`}>{fmtD(r.vch_date)}</td>
                  <td className={`${td} font-medium text-gray-800 max-w-[260px] overflow-hidden text-ellipsis`} title={r.party_name}>{r.party_name}</td>
                  <td className={`${td} text-gray-500`}>{r.ledger_group_name || '—'}</td>
                  <td className={`${td} text-gray-700 max-w-[240px] overflow-hidden text-ellipsis`} title={r.item_name}>{r.item_name}</td>
                  <td className={`${td} text-right tabular-nums`}>{fmtQty(r.qty)}</td>
                  <td className={`${td} text-right tabular-nums text-gray-500`}>{fmt(r.rate)}</td>
                  <td className={`${td} text-right tabular-nums font-medium text-gray-800`}>{fmt(r.amount)}</td>
                  {/* GST Amount column shows the GROSS total: Amount + GST, rounded off to the rupee */}
                  <td className={`${td} text-right tabular-nums text-gray-600`}>{Math.round(Number(r.amount) + Number(r.gst_amount)).toLocaleString('en-IN')}</td>
                  {/* Bill-level: Credit Notes / Receipts allocated against this bill */}
                  <td className={`${td} text-right tabular-nums ${r.credit_note ? 'text-red-600 font-medium' : 'text-gray-300'}`}>
                    {r.credit_note == null ? '' : r.credit_note ? fmt(r.credit_note) : '—'}
                  </td>
                  <td className={`${td} text-right tabular-nums ${r.received ? 'text-teal-700 font-medium' : 'text-gray-300'}`}>
                    {r.received == null ? '' : r.received ? fmt(r.received) : '—'}
                  </td>
                  <td className={`${td} text-center`}>
                    {r.comm_type
                      ? <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${r.comm_type === 'qty' ? 'bg-blue-100 text-blue-700' : r.comm_type === 'cloud' ? 'bg-cyan-100 text-cyan-700' : 'bg-violet-100 text-violet-700'}`}>{commTypeLabel(r.comm_type)}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className={`${td} text-right tabular-nums font-medium ${r.comm_amount == null ? 'text-gray-300' : 'text-violet-700'}`}>
                    {r.comm_amount == null ? '—' : fmt(r.comm_amount)}
                  </td>
                  {/* Net Payable (rounded ₹): On Value → rounded gross − Comm − Credit Note; On Qty → Qty × Comm − Credit Note */}
                  <td className={`${td} text-right tabular-nums font-semibold ${r.net_payable == null ? 'text-gray-300' : Number(r.net_payable) < 0 ? 'text-red-600' : 'text-amber-700'}`}>
                    {r.net_payable == null ? '—' : Math.round(Number(r.net_payable)).toLocaleString('en-IN')}
                  </td>
                  {/* Commission voucher posted against this bill (bill-level, on first line) */}
                  <td className={`${td} font-medium ${r.commission_vch_no ? 'text-violet-700' : 'text-gray-300'}`} title={r.commission_vch_no || undefined}>
                    {r.commission_vch_no || (isBillFirstRow ? '—' : '')}
                  </td>
                  <td className={`${td} text-right tabular-nums font-medium ${r.commission_amount ? 'text-violet-700' : 'text-gray-300'}`}>
                    {r.commission_amount == null ? '' : fmt(r.commission_amount)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="sticky bottom-0 bg-slate-200 font-bold">
                <td className={`${td} bg-slate-200`} colSpan={7}>Total — {fmtQty(totals.parties)} parties · {fmtQty(totals.bills)} bills</td>
                <td className={`${td} bg-slate-200 text-right tabular-nums`}>{fmtQty(totals.qty)}</td>
                <td className={`${td} bg-slate-200`}></td>
                <td className={`${td} bg-slate-200 text-right tabular-nums text-emerald-800`}>{fmt(totals.amount)}</td>
                <td className={`${td} bg-slate-200 text-right tabular-nums`}>{Math.round(totals.amount + totals.gst).toLocaleString('en-IN')}</td>
                <td className={`${td} bg-slate-200 text-right tabular-nums text-red-700`}>{fmt(totals.credit_note)}</td>
                <td className={`${td} bg-slate-200 text-right tabular-nums text-teal-800`}>{fmt(totals.received)}</td>
                <td className={`${td} bg-slate-200`}></td>
                <td className={`${td} bg-slate-200 text-right tabular-nums text-violet-800`}>{fmt(totals.commission)}</td>
                <td className={`${td} bg-slate-200 text-right tabular-nums text-amber-800`}>{Math.round(totals.net_payable).toLocaleString('en-IN')}</td>
                <td className={`${td} bg-slate-200`}></td>
                <td className={`${td} bg-slate-200 text-right tabular-nums text-violet-800`}>{fmt(totals.commission_posted)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
